import React, { useState, useRef, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSave,
  faCopy,
  faSortAlphaDown,
  faIndent,
  faCompressAlt,
  faEye,
  faCode,
  faPlus,
  faTrash,
  faPencilAlt,
} from "@fortawesome/free-solid-svg-icons";
import "./JSONEditor.css";

const JSONEditor = ({ initialContent, onSave, onCancel }) => {
  const [jsonContent, setJsonContent] = useState(
    initialContent || JSON.stringify([{ _id: 1 }], null, 2)
  );
  const [jsonSearchTerm, setJsonSearchTerm] = useState("");
  const [message, setMessage] = useState("");
  const [lineNumberWidth, setLineNumberWidth] = useState(40);
  const [isVisualMode, setIsVisualMode] = useState(false);
  const [excelData, setExcelData] = useState([]);
  const [originalExcelData, setOriginalExcelData] = useState([]);
  const [editingCell, setEditingCell] = useState(null);

  const textareaRef = useRef(null);
  const lineNumbersRef = useRef(null);

  useEffect(() => {
    if (initialContent) {
      try {
        const parsedJson = JSON.parse(initialContent);
        const dataWithId = ensureArray(parsedJson).map((item, index) => ({
          _id: index + 1,
          ...item,
        }));
        setJsonContent(JSON.stringify(dataWithId, null, 2));
        setExcelData(dataWithId);
        setOriginalExcelData(dataWithId); // Store the original data
      } catch (error) {
        console.error("Error parsing initial JSON content:", error);
        setMessage("Error parsing initial JSON content");
        setTimeout(() => setMessage(""), 3000);
      }
    }
    setTimeout(updateLineNumbers, 0);
  }, [initialContent]);

  useEffect(() => {
    updateLineNumbers();
  }, [jsonContent, isVisualMode]);

  const ensureArray = (data) => {
    if (Array.isArray(data)) {
      return data;
    } else if (typeof data === "object" && data !== null) {
      return [data];
    } else {
      return [{ _id: 1, value: data }];
    }
  };
  const updateLineNumbers = () => {
    if (textareaRef.current && lineNumbersRef.current) {
      const lines = textareaRef.current.value.split("\n");
      const lineCount = lines.length;
      const lineNumbers = Array.from(
        { length: lineCount },
        (_, i) => i + 1
      ).join("\n");
      lineNumbersRef.current.value = lineNumbers;

      const digits = lineCount.toString().length;
      const newWidth = Math.max(40, digits * 10 + 20);
      setLineNumberWidth(newWidth);
    }
  };
  const handleScroll = () => {
    if (lineNumbersRef.current && textareaRef.current) {
      lineNumbersRef.current.scrollTop = textareaRef.current.scrollTop;
    }
  };
  const formatJson = () => {
    try {
      const parsedJson = JSON.parse(jsonContent);
      setJsonContent(JSON.stringify(parsedJson, null, 2));
      setExcelData(ensureArray(parsedJson));
    } catch (error) {
      setMessage("Error formatting JSON: " + error.message);
      setTimeout(() => setMessage(""), 3000);
    }
  };
  const minifyJson = () => {
    try {
      const parsedJson = JSON.parse(jsonContent);
      setJsonContent(JSON.stringify(parsedJson));
      setExcelData(ensureArray(parsedJson));
    } catch (error) {
      setMessage("Error minifying JSON: " + error.message);
      setTimeout(() => setMessage(""), 3000);
    }
  };
  const toggleMode = () => {
    setIsVisualMode(!isVisualMode);
    if (!isVisualMode) {
      try {
        const parsedJson = JSON.parse(jsonContent);
        setExcelData(ensureArray(parsedJson));
      } catch (error) {
        console.error(
          "Error parsing JSON when switching to visual mode:",
          error
        );
        setMessage("Error switching to visual mode. Please check your JSON.");
        setTimeout(() => setMessage(""), 3000);
      }
    }
  };
  const sortJson = () => {
    try {
      const parsedJson = JSON.parse(jsonContent);
      const sortedJson = ensureArray(parsedJson).map((obj) =>
        Object.keys(obj)
          .sort()
          .reduce((acc, key) => {
            acc[key] = obj[key];
            return acc;
          }, {})
      );
      setJsonContent(JSON.stringify(sortedJson, null, 2));
      setExcelData(sortedJson);
    } catch (error) {
      setMessage("Error sorting JSON: " + error.message);
      setTimeout(() => setMessage(""), 3000);
    }
  };
  const copyJson = () => {
    navigator.clipboard.writeText(jsonContent).then(() => {
      setMessage("JSON copied to clipboard");
      setTimeout(() => setMessage(""), 3000);
    });
  };
  const searchJson = (searchTerm) => {
    if (!searchTerm) {
      // If search term is empty, restore original data
      setExcelData(originalExcelData);
      setJsonContent(JSON.stringify(originalExcelData, null, 2));
      return;
    }

    const searchInObject = (obj) => {
      return Object.values(obj).some((value) => {
        if (typeof value === "object" && value !== null) {
          // Recursively search in nested objects or arrays
          return searchInObject(value);
        }
        return value
          .toString()
          .toLowerCase()
          .includes(searchTerm.toLowerCase());
      });
    };

    try {
      const searchResult = originalExcelData.filter((obj) =>
        searchInObject(obj)
      );

      setExcelData(searchResult);
      setJsonContent(JSON.stringify(searchResult, null, 2));

      if (searchResult.length === 0) {
        setMessage("No results found");
        setTimeout(() => setMessage(""), 3000);
      } else {
        setMessage(`Found ${searchResult.length} result(s)`);
        setTimeout(() => setMessage(""), 3000);
      }
    } catch (error) {
      setMessage("Error searching JSON: " + error.message);
      setTimeout(() => setMessage(""), 3000);
    }
  };
  const handleSave = () => {
    try {
      JSON.parse(jsonContent);
      onSave(jsonContent);
    } catch (error) {
      setMessage("Invalid JSON. Please correct before saving.");
      setTimeout(() => setMessage(""), 3000);
    }
  };
  const handleCellChange = (rowIndex, path, value) => {
    setExcelData((prevData) => {
      const updatedData = [...prevData];
      const row = { ...updatedData[rowIndex] };

      const pathArray = path.split(".");
      let target = row;
      for (let i = 0; i < pathArray.length - 1; i++) {
        if (!target[pathArray[i]]) {
          target[pathArray[i]] = {};
        }
        target = target[pathArray[i]];
      }
      target[pathArray[pathArray.length - 1]] = value;

      updatedData[rowIndex] = row;
      setJsonContent(JSON.stringify(updatedData, null, 2));
      return updatedData;
    });
  };
  const addRow = () => {
    const newRow = { _id: excelData.length + 1 };
    if (excelData.length > 0) {
      Object.keys(excelData[0]).forEach((key) => {
        if (key !== "_id") {
          newRow[key] = "";
        }
      });
    }
    const updatedData = [...excelData, newRow];
    setExcelData(updatedData);
    setJsonContent(JSON.stringify(updatedData, null, 2));
  };
  const addColumn = (parentPath = "") => {
    const newColumnName = prompt(
      `Enter new column name for ${parentPath || "root"}:`
    );
    if (newColumnName) {
      const updatedData = excelData.map((row) => {
        const newRow = { ...row };
        let target = newRow;
        if (parentPath) {
          const pathArray = parentPath.split(".");
          pathArray.forEach((key) => {
            if (!target[key]) target[key] = {};
            target = target[key];
          });
        }
        // Initialize new column with an empty string
        target[newColumnName] = "";
        return newRow;
      });
      setExcelData(updatedData);
      setJsonContent(JSON.stringify(updatedData, null, 2));
    }
  };
  const deleteRow = (rowIndex) => {
    const updatedData = excelData.filter((_, index) => index !== rowIndex);
    const renumberedData = updatedData.map((row, index) => ({
      ...row,
      _id: index + 1,
    }));
    setExcelData(renumberedData);
    setJsonContent(JSON.stringify(renumberedData, null, 2));
  };
  const deleteColumn = (columnPath) => {
    if (columnPath === "_id") {
      setMessage("Cannot delete _id column");
      setTimeout(() => setMessage(""), 3000);
      return;
    }
    const updatedData = excelData.map((row) => {
      const newRow = { ...row };
      const pathArray = columnPath.split(".");
      const lastKey = pathArray.pop();
      let target = newRow;
      pathArray.forEach((key) => {
        if (target[key]) target = target[key];
      });
      delete target[lastKey];
      return newRow;
    });
    setExcelData(updatedData);
    setJsonContent(JSON.stringify(updatedData, null, 2));
  };
  const editColumnName = (oldPath, newName) => {
    if (oldPath === "_id") {
      setMessage("Cannot edit _id column name");
      setTimeout(() => setMessage(""), 3000);
      return;
    }
    const updatedData = excelData.map((row) => {
      const newRow = { ...row };
      const pathArray = oldPath.split(".");
      const lastKey = pathArray.pop();
      let target = newRow;
      pathArray.forEach((key) => {
        if (!target[key]) target[key] = {};
        target = target[key];
      });
      target[newName] = target[lastKey];
      delete target[lastKey];
      return newRow;
    });
    setExcelData(updatedData);
    setJsonContent(JSON.stringify(updatedData, null, 2));
  };
  const renderExcelHeader = (data, parentPath = "") => {
    return Object.entries(data).map(([key, value]) => {
      const currentPath = parentPath ? `${parentPath}.${key}` : key;
      return (
        <th key={currentPath}>
          {editingCell === `header-${currentPath}` ? (
            <input
              type="text"
              defaultValue={key}
              onBlur={(e) => {
                editColumnName(currentPath, e.target.value);
                setEditingCell(null);
              }}
              autoFocus
            />
          ) : (
            <>
              {key}
              {key !== "_id" && (
                <>
                  <button
                    onClick={() => addColumn(currentPath)}
                    title="Add Child Column"
                    className="edit-button"
                    style={{ marginRight: "45px" }}
                  >
                    <FontAwesomeIcon icon={faPlus} />
                  </button>
                  <button
                    onClick={() => setEditingCell(`header-${currentPath}`)}
                    title="Edit Column Name"
                    className="edit-button"
                    style={{ marginRight: "20px" }}
                  >
                    <FontAwesomeIcon icon={faPencilAlt} />
                  </button>
                  <button
                    onClick={() => deleteColumn(currentPath)}
                    title="Delete Column"
                    className="delete-button"
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </>
              )}
            </>
          )}
          {typeof value === "object" && value !== null && (
            <tr>{renderExcelHeader(value, currentPath)}</tr>
          )}
        </th>
      );
    });
  };
  const renderExcelRow = (row, rowIndex, parentPath = "") => {
    return Object.entries(row).map(([key, value]) => {
      const currentPath = parentPath ? `${parentPath}.${key}` : key;
      return (
        <td key={currentPath} className="excel-cell">
          <div className="cell-content">
            {typeof value === "object" && value !== null ? (
              <table>
                <tbody>
                  <tr>{renderExcelRow(value, rowIndex, currentPath)}</tr>
                </tbody>
              </table>
            ) : key === "_id" ? (
              <span>{value}</span>
            ) : (
              <input
                type="text"
                value={value}
                onChange={(e) =>
                  handleCellChange(rowIndex, currentPath, e.target.value)
                }
              />
            )}
          </div>
        </td>
      );
    });
  };
  const renderExcelMode = () => {
    if (excelData.length === 0) return null;

    return (
      <>
        <div className="excel-mode">
          <table>
            <thead>
              <tr>{renderExcelHeader(excelData[0])}</tr>
            </thead>
            <tbody>
              {excelData.map((row, rowIndex) => (
                <tr key={row._id}>
                  {renderExcelRow(row, rowIndex)}
                  <td style={{ minWidth: "20px" }}>
                    <button
                      onClick={() => deleteRow(rowIndex)}
                      title="Delete Row"
                      className="delete-button"
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="excel-actions">
          <button onClick={addRow} title="Add Row">
            <FontAwesomeIcon icon={faPlus} /> Add Row
          </button>
          <button onClick={() => addColumn()} title="Add Column">
            <FontAwesomeIcon icon={faPlus} /> Add Column
          </button>
        </div>
      </>
    );
  };

  return (
    <div className="json-editor">
      <h3>JSON Editor</h3>
      <div className="json-editor-toolbar">
        <button onClick={formatJson} title="Format JSON">
          <FontAwesomeIcon icon={faIndent} />
        </button>
        <button onClick={minifyJson} title="Minify JSON">
          <FontAwesomeIcon icon={faCompressAlt} />
        </button>
        <button onClick={sortJson} title="Sort JSON">
          <FontAwesomeIcon icon={faSortAlphaDown} />
        </button>
        <button onClick={copyJson} title="Copy JSON">
          <FontAwesomeIcon icon={faCopy} />
        </button>
        <div className="json-search">
          <input
            type="text"
            placeholder="Search JSON..."
            value={jsonSearchTerm}
            onChange={(e) => {
              setJsonSearchTerm(e.target.value);
              searchJson(e.target.value); // Call search function on every keystroke
            }}
          />
        </div>
        <button onClick={toggleMode} title="Toggle Visual/Text Mode">
          <FontAwesomeIcon icon={isVisualMode ? faCode : faEye} />
        </button>
      </div>
      {isVisualMode ? (
        renderExcelMode()
      ) : (
        <div className="json-editor-wrapper">
          <textarea
            ref={lineNumbersRef}
            className="line-numbers"
            readOnly
            style={{ width: `${lineNumberWidth}px` }}
          />
          <textarea
            ref={textareaRef}
            value={jsonContent}
            onChange={(e) => {
              setJsonContent(e.target.value);
              try {
                const parsed = JSON.parse(e.target.value);
                setExcelData(ensureArray(parsed));
              } catch (error) {
                // If parsing fails, don't update excelData
              }
              updateLineNumbers();
            }}
            onScroll={handleScroll}
            className="json-textarea"
            style={{ paddingLeft: `${lineNumberWidth + 10}px` }}
          />
        </div>
      )}
      {message && <p className="json-message">{message}</p>}

      <div className="json-editor-actions">
        <button onClick={handleSave}>
          <FontAwesomeIcon icon={faSave} /> Save JSON
        </button>
        <button onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
};

export default JSONEditor;
